import { ErrorHandler, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ExternalQueryParams,
  ExternalSource,
} from './models/external-query-params';

export enum AppViewMode {
  DEFAULT = 'default',
  PREVIEW = 'preview',
  BUILDER = 'builder',
}

const TAG_ID_PQ201S_CHAR_LENGTH = 8 + 11; // tag id + stuffed chars, e.g. DbkCluRpooooooooooo

export interface Settings {
  // From query params
  viewMode: AppViewMode;

  /**
   * @deprecated Use mode=1 instead
   */
  preview: boolean; // obsolete, use mode instead

  lang: string;
  campaignId: number;
  tagId: string;
  rollId: string;
  machineId: string;
  version: string;
  // from insight, only when in builder mode
  authToken: string;
  parentExperience: string; // e.g. "form:656fe57a903b1100134fbc15" - composed of two parts "exp_type:exp_id"

  // From CMS API
  brandId: string;
  experienceId: string;
  experienceType: any;
  experienceLang: string;

  externalQueryParams?: ExternalQueryParams;

  thirdPartyMappingId?: string;
  thirdPartyMappingSource?: string;

  pq201sAuthCode: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private readonly _settings = new BehaviorSubject<Settings>({
    viewMode: AppViewMode.DEFAULT,
    preview: false,
    lang: '',
    campaignId: null,
    tagId: '',
    rollId: null,
    machineId: '',
    version: '',
    authToken: null,
    parentExperience: null,

    brandId: '',
    experienceId: '',
    experienceType: '',
    experienceLang: '',

    pq201sAuthCode: null,
  });
  readonly settings$ = this._settings.asObservable();

  constructor(private errorHandler: ErrorHandler) {}

  // the getter will return the last value emitted in the _settngs subject
  get settings(): Settings {
    return this._settings.getValue();
  }

  initFromQueryParams() {
    // is client side
    const urlParams = new URLSearchParams(window.location.search);
    let viewMode = urlParams.get('viewMode');
    // backward compatibility
    if (!viewMode) {
      viewMode =
        urlParams.get('preview') === 'true'
          ? AppViewMode.PREVIEW
          : AppViewMode.DEFAULT;
    }

    this.setVersion(urlParams.get('v'));
    this.setViewMode(viewMode);
    this.setLanguage(urlParams.get('lang'));

    // Parameters used in default mode
    this.setCampaignId(urlParams.get('campaignId'));
    this.setTagId(urlParams.get('tagId'));
    this.setRollId(urlParams.get('rollId'));
    this.setMachineId(urlParams.get('machineId'));
    this.setAuthToken(urlParams.get('authToken')); // received from insight - Builder use only

    this.setParentExperience(urlParams.get('pExp'));

    this.setExternalQueryParams(urlParams);

    const thirdPartyMappingId = urlParams.get('thirdPartyMappingId');
    const thirdPartyMappingSource = urlParams.get('thirdPartyMappingSource');
    if (!!thirdPartyMappingId && !!thirdPartyMappingSource) {
      this.setThirdPartyMapping(thirdPartyMappingId, thirdPartyMappingSource);
    }

    /**
     * Checks if all the required parameters are present and logs an error if not.
     */
    if (this.settings.viewMode === AppViewMode.DEFAULT) {
      const missingParams = [];
      const { campaignId, tagId, rollId } = this.settings;
      if (!campaignId) {
        missingParams.push('campaignId');
      }
      if (!tagId) {
        missingParams.push('tagId');
      }
      if (!rollId) {
        missingParams.push('rollId');
      }
      if (missingParams.length) {
        /**
         * Gather more information - how did the user land to this URL?
         * ISD-8851 - this is a long standing bug that we can't figure out!
         **/

        this.errorHandler.handleError(
          new Error(
            `There are missing parameters in the settings initialization. Missing params: ${missingParams.join(
              ', '
            )}. Referrer URL: ${document.referrer}.`
          )
        );
      }
    }
  }

  toQueryParams() {
    const queryParams = new Map<string, string>();
    const {
      viewMode,
      version,
      lang,
      campaignId,
      tagId,
      rollId,
      machineId,
      authToken,
      parentExperience,
      thirdPartyMappingId,
      thirdPartyMappingSource,
    } = this.settings;

    viewMode &&
      viewMode !== AppViewMode.DEFAULT &&
      queryParams.set('viewMode', viewMode);

    version && queryParams.set('v', version);
    lang && queryParams.set('lang', lang);

    campaignId && queryParams.set('campaignId', campaignId.toString());
    tagId && queryParams.set('tagId', tagId.toString());
    rollId && queryParams.set('rollId', rollId.toString());
    machineId && queryParams.set('machineId', machineId);
    authToken && queryParams.set('authToken', authToken);
    parentExperience && queryParams.set('pExp', parentExperience);
    thirdPartyMappingId &&
      queryParams.set('thirdPartyMappingId', thirdPartyMappingId);
    thirdPartyMappingSource &&
      queryParams.set('thirdPartyMappingSource', thirdPartyMappingSource);

    return queryParams;
  }

  setViewMode(mode: string) {
    let modeEnum = AppViewMode.DEFAULT;
    if (mode === 'preview') {
      modeEnum = AppViewMode.PREVIEW;
    } else if (mode === 'builder') {
      modeEnum = AppViewMode.BUILDER;
    }

    this._settings.next({
      ...this.settings,
      viewMode: modeEnum,
      preview: mode === AppViewMode.BUILDER || mode === AppViewMode.PREVIEW,
    });
  }

  setCampaignId(val: string) {
    const campaignId = !!val ? +val : null;
    this._settings.next({
      ...this.settings,
      campaignId: campaignId,
    });
  }

  setBrandId(val: string) {
    this._settings.next({
      ...this.settings,
      brandId: val,
    });
  }

  setExperienceId(val: string) {
    this._settings.next({
      ...this.settings,
      experienceId: val,
    });
  }

  setExperienceType(val: string) {
    this._settings.next({
      ...this.settings,
      experienceType: val,
    });
  }

  setExperienceLanguage(val: string) {
    this._settings.next({
      ...this.settings,
      experienceLang: val,
    });
  }

  setLanguage(val: string) {
    this._settings.next({
      ...this.settings,
      lang: val,
    });
  }

  setTagId(val: string) {
    let tagId = val;
    let pq201sAuthCode = null;
    if (val?.length === TAG_ID_PQ201S_CHAR_LENGTH) {
      tagId = val.substring(0, 8);
      pq201sAuthCode = val.substring(8);
    }
    this._settings.next({
      ...this.settings,
      tagId,
      pq201sAuthCode,
    });
  }

  setRollId(val: string) {
    this._settings.next({
      ...this.settings,
      rollId: val,
    });
  }

  setMachineId(val: string) {
    this._settings.next({
      ...this.settings,
      machineId: val,
    });
  }

  setAuthToken(val: string) {
    this._settings.next({
      ...this.settings,
      authToken: val,
    });
  }

  setThirdPartyMapping(id: string, source: string) {
    this._settings.next({
      ...this.settings,
      thirdPartyMappingId: id,
      thirdPartyMappingSource: source,
    });
  }

  /**
   *
   * @param val (unpublished if the experience is live and the language is active)
   * Setting the version to unpublished would fetch all the unpublished content in an experience
   */
  setVersion(val: string) {
    if (val) {
      this._settings.next({
        ...this.settings,
        version: val,
      });
    }
  }

  setParentExperience(val: string) {
    this._settings.next({
      ...this.settings,
      parentExperience: val,
    });
  }

  /**
   * Sets the query params that are not internal to kyudo.
   * All query params that start with the value of the externalSource would be regarded as a query param from an external source.
   */
  setExternalQueryParams(urlParams: URLSearchParams) {
    const externalSource = urlParams.get('externalSource') as ExternalSource;

    // Set external query params only if the externalSource is defined
    if (!!externalSource) {
      const filteredParams = {};

      // Fetch all the query params that start with the external source
      urlParams.forEach((value, key) => {
        if (key.startsWith(externalSource)) {
          filteredParams[key] = value;
        }
      });

      this._settings.next({
        ...this.settings,
        externalQueryParams: {
          externalSource: externalSource,
          ...filteredParams,
        },
      });
    }
  }
}
